import type { TextDirection, UIMessage } from '../../bot-core/types/chat.types';

/**
 * Conversation languages whose assistant bubbles render right-to-left.
 *
 * Keep in step with RTL_CONVERSATION_LANGUAGES in sky360-ms
 * `bot-agent/channel-text.ts`, which adds the per-line RLMs on WhatsApp and the
 * CLI for the same languages.
 */
const RTL_CONVERSATION_LANGUAGES: ReadonlySet<string> = new Set(['he', 'ar', 'fa']);

/** Right-to-left letters. Same ranges as RTL_CHAR_RE in channel-text.ts. */
const RTL_CHAR_RE = /[֐-ࣿיִ-﷿ﹰ-ﻼ]/;

export function isRtlConversationLanguage(lang: string | null | undefined): boolean {
  if (!lang) return false;
  // The BE sends a primary subtag. Normalizing anyway lets 'he-IL' or 'he_il'
  // work too.
  return RTL_CONVERSATION_LANGUAGES.has(lang.toLowerCase().split(/[-_]/)[0]);
}

/**
 * The `dir` for one bubble in the bot widget.
 *
 * In a Hebrew conversation, an assistant bubble that contains Hebrew is forced
 * to 'rtl'. With plain `dir="auto"`, a reply that opens with a Latin name
 * ("El Al 541 …", "Rooms Hotel …") flips the whole bubble to left-to-right.
 *
 * Everything else stays 'auto':
 *   - user and system bubbles. The user types in whatever script they like, and
 *     system notices are already localized.
 *   - an assistant bubble with no RTL letters, such as the English turns from
 *     before a language switch, or a reply where the model slipped into English.
 *     Forcing RTL on those would right-align English text and move its
 *     punctuation to the wrong side.
 */
export function botMessageDir(
  message: Pick<UIMessage, 'role' | 'text'>,
  conversationLanguage: string | null | undefined,
): TextDirection {
  if (message.role !== 'assistant') return 'auto';
  if (!isRtlConversationLanguage(conversationLanguage)) return 'auto';
  return RTL_CHAR_RE.test(message.text ?? '') ? 'rtl' : 'auto';
}
